import { Box, MenuItem, TextField } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { Controller, useFormContext } from 'react-hook-form';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import {
  MEDICATION_STATUS_OPTIONS,
  MEDICATION_STOP_REASON_OPTIONS,
  OTHER_OPTION,
} from './medicationFormOptions';
import { SPAN_WIDE } from './medicationFormLayout';
import { selectTestId } from './selectTestId';

interface MedicationStatusSectionProps {
  disabled?: boolean;
}

/**
 * Whether the patient is still on it. Stopping asks for a reason, because
 * "odstawiony" without one tells the next vet nothing about whether to try it
 * again.
 */
export default function MedicationStatusSection({
  disabled,
}: MedicationStatusSectionProps) {
  const { control, watch } = useFormContext();
  const status = watch('status');
  const stopReason = watch('stopReason');
  const isStopped = status === 'STOPPED';

  return (
    <>
      <Box sx={SPAN_WIDE}>
        <RHFSelect
          name="status"
          label={translateKey('medication.form.status')}
          disabled={disabled}
          SelectProps={{
            native: false,
            SelectDisplayProps: selectTestId('medication-select-status'),
          }}
        >
          {MEDICATION_STATUS_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              data-testid={`medication-option-${option.value}`}
            >
              {option.label}
            </MenuItem>
          ))}
        </RHFSelect>
      </Box>

      {isStopped && (
        <>
          <RHFSelect
            name="stopReason"
            label={translateKey('medication.form.stopReason')}
            required
            disabled={disabled}
            SelectProps={{
              native: false,
              SelectDisplayProps: selectTestId(
                'medication-select-stopReason',
              ),
            }}
          >
            <MenuItem value="">-</MenuItem>
            {MEDICATION_STOP_REASON_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                data-testid={`medication-option-${option.value}`}
              >
                {option.label}
              </MenuItem>
            ))}
          </RHFSelect>

          {stopReason === OTHER_OPTION && (
            <Box sx={SPAN_WIDE}>
              <RHFTextField
                name="stopReasonNote"
                fullWidth
                label={translateKey('medication.form.stopReasonNote')}
                required
                disabled={disabled}
              />
            </Box>
          )}

          <Controller
            name="endedAt"
            control={control}
            render={({ field, fieldState }) => (
              <DesktopDatePicker
                label={translateKey('medication.form.endedAt')}
                value={field.value}
                onChange={field.onChange}
                disabled={disabled}
                inputFormat="dd.MM.yyyy"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ??
                      translateKey('medication.form.endedAtHint')
                    }
                  />
                )}
              />
            )}
          />
        </>
      )}
    </>
  );
}
